import { useTranslation } from 'react-i18next';

import { View } from '@actual-app/components/view';

import { TarjetaInforme } from '#components/mobile/informes/comunes';
import type { PropsWidget } from '#components/mobile/inicio/comunes';
import { color } from '#components/mobile/ui/tokens';
import { useFormat } from '#hooks/useFormat';
import { useNavigate } from '#hooks/useNavigate';

import { FilaDato } from './FilaDato';
import { categoriasQueSePasan } from './ritmo';
import type { SaldoCategoria } from './ritmo';
import { useGastoDelMes } from './useGastoDelMes';
import { useSaldosMes } from './useSaldosMes';

/**
 * «Se pasan»: las categorías en rojo y las que, a este ritmo, se pasarán
 * antes de acabar el mes. Ancha = 3 filas; grande = 5.
 */
export function TarjetaSePasan({ tamano }: PropsWidget) {
  const { t } = useTranslation();
  const format = useFormat();
  const navigate = useNavigate();
  const fmt = (v: number) => format(v, 'financial-no-decimals');
  const { mes, dia, diasMes, categorias, excluidas, analisis, isLoading } =
    useGastoDelMes();
  const { saldos, isLoading: cargandoSaldos } = useSaldosMes(mes);
  const cargando = isLoading || cargandoSaldos;

  const fijos = new Set(
    analisis.filas.filter(f => f.esFijo).map(f => f.categoria),
  );
  const lista: SaldoCategoria[] = [];
  for (const [id, celdas] of saldos ?? []) {
    const info = categorias.get(id);
    if (!info || info.esIngreso || info.oculta || excluidas.has(id)) {
      continue;
    }
    const gastado = Math.max(-celdas.movido, 0);
    lista.push({
      id,
      gastado,
      saldo: celdas.saldo,
      disponible: celdas.saldo + gastado,
      esFijo: fijos.has(id),
    });
  }
  const { pasadas, justas, bien } = categoriasQueSePasan(lista, dia, diasMes);
  const total = pasadas.length + justas.length + bien;
  const filas = [...pasadas, ...justas].slice(0, tamano === 'grande' ? 5 : 3);
  const nombreDe = (id: string) => categorias.get(id)?.nombre ?? t('Unknown');

  const valor = cargando
    ? '…'
    : total === 0
      ? '—'
      : t('{{over}} of {{total}} over', { over: pasadas.length, total });
  const frase = cargando
    ? ''
    : total === 0
      ? t('Assign money in the Plan to see which categories go over.')
      : pasadas.length === 0 && justas.length === 0
        ? t('All within the plan. Well steered.')
        : justas.length > 0
          ? t('{{count}} more will be tight at this pace.', {
              count: justas.length,
            })
          : t('Cover them with money from a category that has some left.');

  return (
    <TarjetaInforme
      enRejilla
      data-testid="tarjeta-se-pasan"
      titulo={t('Going over')}
      valor={valor}
      frase={tamano === 'compacto' ? '' : frase}
      tono={
        cargando || total === 0
          ? 'neutro'
          : pasadas.length > 0
            ? 'mal'
            : justas.length > 0
              ? 'aviso'
              : 'bien'
      }
      onPress={() => void navigate('/reports/gasto')}
    >
      {!cargando && filas.length > 0 && tamano !== 'compacto' && (
        <View>
          {filas.map(f => (
            <FilaDato
              key={f.id}
              colorPunto={f.tipo === 'pasada' ? color.bad : color.warn}
              nombre={nombreDe(f.id)}
              nota={
                f.tipo === 'pasada'
                  ? t('+{{amount}}', { amount: fmt(f.importe) })
                  : t('{{amount}} left', { amount: fmt(f.importe) })
              }
              colorNota={f.tipo === 'pasada' ? color.bad : color.warn}
              valor={`${fmt(f.gastado)} / ${fmt(f.disponible)}`}
            />
          ))}
        </View>
      )}
    </TarjetaInforme>
  );
}
